import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">🔄 Loading dashboard...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            👨‍💼 Admin Dashboard
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Monitor and manage the entire platform
          </p>
        </div>

        {/* Stats Grid */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)'
          }}>
            📊 Platform Overview
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
            gap: 'var(--space-lg)'
          }} className="admin-stats-grid">
            <div className="stat-card" style={{ borderLeftColor: 'var(--primary)' }}>
              <div className="stat-icon">📦</div>
              <div className="stat-value">{stats?.totalOrders || 0}</div>
              <div className="stat-label">Total Orders</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'var(--secondary)' }}>
              <div className="stat-icon">🚀</div>
              <div className="stat-value">{stats?.todayOrders || 0}</div>
              <div className="stat-label">Today's Orders</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'var(--success)' }}>
              <div className="stat-icon">🏪</div>
              <div className="stat-value">{stats?.totalRestaurants || 0}</div>
              <div className="stat-label">Restaurants</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'var(--warning)' }}>
              <div className="stat-icon">👥</div>
              <div className="stat-value">{stats?.totalUsers || 0}</div>
              <div className="stat-label">Users</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'rgba(78, 205, 196, 1)' }}>
              <div className="stat-icon">🚴</div>
              <div className="stat-value">{stats?.totalDeliveryPartners || 0}</div>
              <div className="stat-label">Delivery Partners</div>
            </div>

            <div className="stat-card" style={{
              borderLeftColor: stats?.pendingApprovals > 0 ? 'var(--danger)' : 'var(--gray-400)',
              background: stats?.pendingApprovals > 0 ? 'rgba(255, 107, 107, 0.05)' : 'white'
            }}>
              <div className="stat-icon">⏳</div>
              <div className="stat-value" style={{
                color: stats?.pendingApprovals > 0 ? 'var(--danger)' : 'var(--gray-700)'
              }}>
                {stats?.pendingApprovals || 0}
              </div>
              <div className="stat-label">Pending Approvals</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <h3 style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: '700',
          marginBottom: 'var(--space-lg)',
          color: 'var(--gray-900)',
          textAlign: 'center'
        }}>
          ⚡ Quick Actions
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--space-lg)'
        }}>
          <Link to="/admin/orders" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'var(--gradient-primary)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>📦</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                All Orders
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                View and manage all platform orders
              </p>
            </div>
          </Link>

          <Link to="/admin/approvals" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: stats?.pendingApprovals > 0
                ? 'linear-gradient(135deg, #ff6b6b 0%, #e85a5a 100%)'
                : 'var(--gradient-secondary)',
              color: 'white',
              border: 'none',
              height: '100%',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {stats?.pendingApprovals > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 'var(--space-md)',
                  right: 'var(--space-md)',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'white',
                  color: 'var(--danger)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '800',
                  fontSize: 'var(--font-size-base)',
                  boxShadow: 'var(--shadow-md)'
                }}>
                  {stats.pendingApprovals}
                </div>
              )}
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>✅</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Approvals
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Approve delivery partners
              </p>
            </div>
          </Link>

          <Link to="/admin/performance" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>📊</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Performance
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                View performance metrics
              </p>
            </div>
          </Link>

          <Link to="/admin/restaurant-reviews" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>📝</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Restaurant Reviews
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Review restaurant documents
              </p>
            </div>
          </Link>

          <Link to="/admin/subscriptions" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>💳</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Subscriptions
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Manage restaurant plans
              </p>
            </div>
          </Link>

          <Link to="/admin/cities" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #16a085 0%, #138d75 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>🏙️</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                City Management
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Manage available cities
              </p>
            </div>
          </Link>

          <Link to="/admin/location-manager" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>📍</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Location Manager
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Update entity locations on map
              </p>
            </div>
          </Link>

          <Link to="/admin/payouts" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>💰</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Payouts
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Manage restaurant & delivery payouts
              </p>
            </div>
          </Link>

          <Link to="/admin/delivery-fee-settings" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>🚚</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Delivery Fees
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Configure fees by city & region
              </p>
            </div>
          </Link>

          <Link to="/admin/infrastructure" style={{ textDecoration: 'none' }}>
            <div className="card fade-in" style={{
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              color: 'white',
              border: '2px solid #3b82f6',
              height: '100%'
            }}>
              <div style={{ fontSize: '64px', marginBottom: 'var(--space-md)' }}>🖥️</div>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Infrastructure
              </h2>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Monitor servers & load balancing
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
